import React from 'react';
import { Col, Row, Table } from 'react-bootstrap';
import IFormStep from 'interfaces/IFormStep';
import HelpButton from '../../../Form/HelpButton';
import _ from 'lodash';
import IActivity from '../../../../interfaces/IActivity';
import ActivityModal from './ActivityModal';
import ActivityActions from './ActivityActions';
import dateFormat from 'dateformat';

const Activities: React.FC<IFormStep> = ({ formData, requirements, handleInputChange, models }) => {
    const help = requirements.config?.help && requirements.config?.help['activities'];
    const activities = formData.activities ?? [];

    const numberOfActivitiesRequired = 90;

    const sortActivities = (activities: IActivity[]) => {
        return _.sortBy(activities, (a) => {
            return a['date'];
        });
    };

    const generateNewActivityTemplate = (template?: IActivity): IActivity => {
        if (template) {
            return {
                provider: template.provider,
            };
        }

        if (!activities?.length) {
            return {};
        }

        const { provider } = activities[activities.length - 1];

        return {
            provider,
        };
    };

    const [editableActivity, setEditableActivity] = React.useState<IActivity>(
        generateNewActivityTemplate()
    );
    const [keepModalOpen, setKeepModalOpen] = React.useState(false);

    const handleEditableActivityUpdate = (name: string, value: any) => {
        setEditableActivity((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleActivitiesChange = (value: IActivity[]) => {
        handleInputChange('activities', sortActivities(value));
    };

    const handleUpdateActivity = (index: number) => {
        const updatedActivities = activities.map((cc, i) => {
            if (i === index) {
                return editableActivity;
            }

            return cc;
        });
        handleActivitiesChange(updatedActivities);
    };

    const handleAddNewActivity = () => {
        handleActivitiesChange(activities.concat([editableActivity]));
        setEditableActivity(generateNewActivityTemplate(editableActivity));
    };

    const handleDeleteActivity = (index: number) => {
        handleActivitiesChange(_.filter(activities, (_item, i) => i !== index));
        setEditableActivity(generateNewActivityTemplate());
    };

    const isValid = (activity: IActivity) => {
        return models.formValidator.validateActivity(activity);
    };

    const formatDate = (inputDate: string) => dateFormat(new Date(inputDate), 'yyyy-mm-dd');

    return (
        <>
            <Row>
                <h3 className="fw-bold mb-3">
                    EMDR activity {help && <HelpButton title={'Activities'} content={help} />}
                </h3>
            </Row>
            <Row className={'mb-3 col-10 text-black fw-normal'}>
                <h4 className={'fw-normal'}>
                    Add details for each activity separately. A new record entry form will appear
                    automatically when you complete a record.
                </h4>
            </Row>
            {activities.length > 0 && (
                <Row className={'mb-4'}>
                    <Col xs={12}>
                        <Table responsive striped className={'activity-table'}>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Date</th>
                                    <th>Event & Title</th>
                                    <th>Provider</th>
                                    <th>Speaker(s)</th>
                                    <th>Specific EMDR Credits</th>
                                    <th>Related Credits/Hours</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody className="bg-white">
                                {activities.map((activity, index) => (
                                    <tr key={'activity-' + index}>
                                        <td>{index + 1}</td>
                                        <td>{formatDate(activity.date!)}</td>
                                        <td>{activity.eventAndTitle}</td>
                                        <td>{activity.provider}</td>
                                        <td>{activity.speakers}</td>
                                        <td>{activity.specificEmdrCredits}</td>
                                        <td>{activity.relatedCreditsHours}</td>
                                        <td>
                                            <ActivityActions
                                                index={index}
                                                activity={activity}
                                                editableActivity={editableActivity}
                                                handleUpdateActivity={handleUpdateActivity}
                                                handleDeleteActivity={handleDeleteActivity}
                                                handleEditableActivityUpdate={
                                                    handleEditableActivityUpdate
                                                }
                                                generateNewActivityTemplate={
                                                    generateNewActivityTemplate
                                                }
                                                setEditableActivity={setEditableActivity}
                                                isValid={isValid}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            )}
            {activities?.length < numberOfActivitiesRequired && (
                <Row>
                    <Col xs={5}>
                        <ActivityModal
                            editableActivity={editableActivity}
                            handleEditableActivityUpdate={handleEditableActivityUpdate}
                            handleAddNewActivity={handleAddNewActivity}
                            setEditableActivity={setEditableActivity}
                            isValid={isValid}
                            generateNewActivityTemplate={generateNewActivityTemplate}
                            keepModalOpen={keepModalOpen}
                            setKeepModalOpen={setKeepModalOpen}
                        />
                    </Col>
                </Row>
            )}
        </>
    );
};

export default Activities;
